import { useSyncExternalStore } from "react";
import { readSession, removeSession, writeSession } from "@/helpers/safeStorage";

// Les fenêtres flottantes ouvertes, et si chacune est réduite : une discussion
// de la messagerie (`discussion`), ou une question en cours d'écriture sur une
// session de cours (`compose`). La bulle qui les ouvre vit dans la barre du haut de
// chaque page, les fenêtres sont montées une seule fois à la racine : un
// magasin hors de React les relie sans faire remonter un contexte de plus.
//
// L'état est gardé dans le stockage de session : recharger la page retrouve
// les fenêtres telles qu'on les a laissées, ouvertes ou réduites. Il reste
// propre à l'onglet, un nouvel onglet ne s'ouvre pas avec des discussions
// par-dessus.
const STORAGE_KEY = "messenger_window";
const MOBILE_BREAKPOINT = 768;
// Largeur d'une fenêtre et de sa gouttière, pour savoir combien tiennent.
const WINDOW_SLOT_PX = 412;
// Une fenêtre agrandie (un guide, ou le bouton de l'en-tête) prend plus de place.
const WIDE_WINDOW_SLOT_PX = 572;
const MAX_WINDOWS = 3;
const CLOSED = { windows: [], ownerId: null };

/** Ce qui distingue une fenêtre : sa discussion, ou la question qu'on y écrit. */
export const windowId = (entry) =>
    entry.compose ? entry.compose.id : entry.discussion.id;

/**
 * La fenêtre est-elle agrandie ? C'est le choix de la personne (bouton de
 * l'en-tête) ; tant qu'elle n'a rien choisi, un guide s'ouvre en grand et le
 * reste à la taille normale.
 */
export const isWindowExpanded = (entry) =>
    entry.expanded ?? Boolean(entry.compose?.isGuide);

/**
 * L'emplacement d'une fenêtre dans la rangée. Il survit au passage de la
 * question au fil : le cadre reste en place, seul son contenu change.
 */
export const windowSlot = (entry) => entry.slot ?? windowId(entry);

let state = CLOSED;
let restored = false;
const listeners = new Set();

// Le coin bas droit est partagé : quand la carte des nouveautés s'y affiche,
// elle réserve sa largeur et les fenêtres se rangent à sa gauche.
let cornerReserved = 0;
const cornerListeners = new Set();

export const setCornerReserved = (px) => {
    const next = Math.max(0, Number(px) || 0);
    if (next === cornerReserved) return;
    cornerReserved = next;
    cornerListeners.forEach((listener) => listener());
};

const subscribeCorner = (listener) => {
    cornerListeners.add(listener);
    return () => cornerListeners.delete(listener);
};

export const useCornerReserved = () =>
    useSyncExternalStore(subscribeCorner, () => cornerReserved, () => 0);

/**
 * Combien de fenêtres tiennent dans une largeur d'écran : trois au plus, moins
 * si la place manque ou si le coin est occupé, une seule sur téléphone où la
 * discussion prend tout l'écran.
 */
export const windowCapacity = (width, reserved = 0) => {
    if (width < MOBILE_BREAKPOINT) return 1;
    return Math.max(
        1,
        Math.min(MAX_WINDOWS, Math.floor((width - 16 - reserved) / WINDOW_SLOT_PX)),
    );
};

/**
 * Les fenêtres qui tiennent à l'écran, des plus récentes aux plus anciennes :
 * celle d'un guide prend plus de place qu'une autre.
 */
export const windowsThatFit = (windows, width, reserved = 0) => {
    const max = windowCapacity(width, reserved);
    const available = width - 16 - reserved;
    const kept = [];
    let used = 0;
    for (let i = windows.length - 1; i >= 0 && kept.length < max; i -= 1) {
        const entry = windows[i];
        const slot =
            isWindowExpanded(entry) && !entry.minimized
                ? WIDE_WINDOW_SLOT_PX
                : WINDOW_SLOT_PX;
        if (kept.length > 0 && used + slot > available) break;
        used += slot;
        kept.unshift(entry);
    }
    return kept;
};

const capacity = () =>
    typeof window === "undefined"
        ? 1
        : windowCapacity(window.innerWidth, cornerReserved);

const persist = () => {
    if (state.windows.length === 0) {
        removeSession(STORAGE_KEY);
        return;
    }
    // Les « vu » ne sont pas gardés : le back les efface à chaque nouveau
    // message, une copie relue après rechargement pourrait annoncer « vu »
    // sous un message que personne n'a encore ouvert.
    writeSession(
        STORAGE_KEY,
        JSON.stringify({
            ownerId: state.ownerId,
            windows: state.windows.map((entry) => ({
                minimized: entry.minimized,
                // Absent tant que la personne n'a pas choisi de taille.
                expanded: entry.expanded,
                ...(entry.compose
                    ? { compose: entry.compose }
                    : { discussion: { ...entry.discussion, seenBy: [] } }),
            })),
        }),
    );
};

const setState = (next) => {
    state = next;
    persist();
    listeners.forEach((listener) => listener());
};

// Relu au premier abonnement, donc après l'hydratation : le serveur a rendu
// « fermé », le navigateur doit commencer du même état.
const restore = () => {
    if (restored) return;
    restored = true;
    try {
        const saved = JSON.parse(readSession(STORAGE_KEY) || "null");
        const windows = (saved?.windows || [])
            .filter((entry) => entry?.discussion?.id || entry?.compose?.id)
            .slice(-MAX_WINDOWS)
            .map((entry) => ({
                ...(entry.compose
                    ? { compose: entry.compose }
                    : { discussion: entry.discussion }),
                minimized: Boolean(entry.minimized),
                ...(typeof entry.expanded === "boolean"
                    ? { expanded: entry.expanded }
                    : {}),
            }));
        if (windows.length > 0) {
            state = { windows, ownerId: saved.ownerId ?? null };
        }
    } catch {
        removeSession(STORAGE_KEY);
    }
};

const subscribe = (listener) => {
    const wasRestored = restored;
    restore();
    listeners.add(listener);
    // L'état relu n'était pas celui du premier rendu : on prévient.
    if (!wasRestored && state !== CLOSED) queueMicrotask(listener);
    return () => listeners.delete(listener);
};

const getSnapshot = () => state;
const getServerSnapshot = () => CLOSED;

/**
 * Ouvre une discussion dans une fenêtre. Déjà ouverte, elle est simplement
 * agrandie. Au-delà de ce que l'écran peut accueillir, la plus ancienne cède
 * sa place.
 *
 * ownerId : le compte qui ouvre. Un autre compte connecté dans le même onglet
 * ne doit pas retrouver les discussions du précédent.
 */
const openWindow = (opened, ownerId) => {
    restore();
    const owner = ownerId ?? null;
    const sameOwner = state.ownerId === owner;
    const current = sameOwner ? state.windows : [];
    const id = windowId(opened);
    const existing = current.find((entry) => windowId(entry) == id);
    const windows = existing
        ? current.map((entry) =>
              entry === existing ? { ...entry, minimized: false } : entry,
          )
        : [...current, { ...opened, minimized: false }].slice(-capacity());
    setState({ windows, ownerId: owner });
};

export const openMessengerWindow = (discussion, ownerId) =>
    openWindow({ discussion }, ownerId);

/**
 * Ouvre la fenêtre où l'on écrit une question (ou un guide) sur une session.
 * Une seule par session : la rouvrir ramène celle qui existe, avec son texte.
 *
 * compose : { sessionId, sessionTitle, slug, partNumber, sessionNumber, time,
 * isGuide } et, pour un accélérateur, { program, moduleNumber, programSlug }.
 */
export const openComposeWindow = (compose, ownerId) =>
    openWindow(
        {
            compose: { ...compose, id: `compose-${compose.sessionId}` },
            // Propre à cette ouverture : le fil qui héritera de l'emplacement
            // peut rester ouvert pendant qu'on écrit une autre question sur
            // la même session.
            slot: `compose-${compose.sessionId}-${Date.now()}`,
        },
        ownerId,
    );

/**
 * Émis sur `window` quand une question ou un guide vient d'être publié depuis
 * la fenêtre : { sessionId, discussions }. L'écran de la session s'en sert
 * pour mettre sa liste à jour.
 */
export const DISCUSSION_CREATED_EVENT = "believemy:discussion-created";

/** Question ou guide : le choix est gardé, la fenêtre d'un guide est plus large. */
export const setComposeWindowKind = (composeId, isGuide) => {
    const target = state.windows.find(
        (entry) => entry.compose?.id === composeId,
    );
    if (!target || Boolean(target.compose.isGuide) === isGuide) return;
    setState({
        ...state,
        windows: state.windows.map((entry) =>
            // La taille repart du réglage par défaut du type choisi.
            entry === target
                ? {
                      ...entry,
                      expanded: undefined,
                      compose: { ...entry.compose, isGuide },
                  }
                : entry,
        ),
    });
};

/**
 * La question vient d'être publiée : sa fenêtre devient le fil de la
 * discussion, à la même place dans la rangée.
 */
export const replaceComposeWithDiscussion = (composeId, discussion) => {
    const target = state.windows.find(
        (entry) => entry.compose?.id === composeId,
    );
    if (!target) return;
    const windows = state.windows
        .filter(
            (entry) => entry === target || windowId(entry) != discussion.id,
        )
        .map((entry) =>
            entry === target
                ? {
                      discussion,
                      minimized: false,
                      slot: windowSlot(target),
                      // Seul un choix explicite suit : un guide ouvert en
                      // grand par défaut redonne un fil à la taille normale.
                      expanded: target.expanded,
                  }
                : entry,
        );
    setState({ ...state, windows });
};

/**
 * Ferme les discussions et garde les questions en cours d'écriture : sur la
 * page /messenger, le fil est déjà à l'écran, pas le brouillon.
 */
export const closeDiscussionWindows = () => {
    const windows = state.windows.filter((entry) => entry.compose);
    if (windows.length === state.windows.length) return;
    setState(windows.length === 0 ? CLOSED : { ...state, windows });
};

/** Ferme une fenêtre, ou toutes si aucun identifiant n'est donné. */
export const closeMessengerWindow = (discussionId) => {
    if (state.windows.length === 0) return;
    if (discussionId === undefined) {
        setState(CLOSED);
        return;
    }
    const windows = state.windows.filter(
        (entry) => windowId(entry) != discussionId,
    );
    setState(windows.length === 0 ? CLOSED : { ...state, windows });
};

export const setMessengerWindowMinimized = (discussionId, minimized) => {
    const target = state.windows.find(
        (entry) => windowId(entry) == discussionId,
    );
    if (!target || target.minimized === minimized) return;
    setState({
        ...state,
        windows: state.windows.map((entry) =>
            entry === target ? { ...entry, minimized } : entry,
        ),
    });
};

/** Agrandit une fenêtre ou la ramène à sa taille normale. */
export const setMessengerWindowExpanded = (id, expanded) => {
    const target = state.windows.find((entry) => windowId(entry) == id);
    if (!target || isWindowExpanded(target) === expanded) return;
    setState({
        ...state,
        windows: state.windows.map((entry) =>
            entry === target ? { ...entry, expanded } : entry,
        ),
    });
};

const useMessengerWindowState = () =>
    useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

export const useMessengerWindows = () => useMessengerWindowState().windows;

export const useMessengerWindowCount = () =>
    useMessengerWindowState().windows.length;

export const useMessengerWindowOwnerId = () => useMessengerWindowState().ownerId;

// Passage de témoin vers la page /messenger : « Ouvrir en grand » y dépose la
// discussion de la fenêtre, la messagerie la sélectionne en arrivant. L'objet
// entier est transmis, la discussion n'a donc pas besoin de figurer parmi les
// vingt premières de la liste.
let handoff = null;

export const handOffToMessenger = (discussion) => {
    handoff = discussion;
};

export const peekMessengerHandoff = () => handoff;

export const clearMessengerHandoff = () => {
    handoff = null;
};
